/**
 * Deep Dive Videos — Web Component Controller
 *
 * Fetches the JSON video index, handles search, category filtering, pagination,
 * and the modal video player (Video.js for HLS, YouTube iframe for YT).
 *
 * Forked from Expert Review Videos — uses category-based filtering instead of
 * strength/lineage.
 *
 * @typedef {Object} VideoRecord
 * @property {string} id - Article handle (unique key)
 * @property {string} title - Article title
 * @property {string} article_url - Path to full article
 * @property {string} published_at - ISO 8601 date
 * @property {'hls'|'youtube'} provider - Video provider
 * @property {string|null} stream_url - HLS manifest URL
 * @property {string|null} poster_url - Thumbnail URL
 * @property {string|null} youtube_id - YouTube video ID
 * @property {'101'|'brand'|'news'} category - Blog category key
 * @property {string} category_label - Human-readable category name
 * @property {string[]} tags - Raw article tags
 * @property {string} search_text - Pre-built search corpus
 */

class DeepDiveVideos extends HTMLElement {
  /** @type {VideoRecord[]} */
  #allVideos = [];

  /** @type {VideoRecord[]} */
  #filteredVideos = [];

  /** @type {string} */
  #currentFilter = 'all';

  /** @type {string} */
  #currentQuery = '';

  /** @type {number} */
  #currentPage = 1;

  /** @type {number} */
  #perPage = 24;

  /** @type {Object|null} Active Video.js instance or YouTube ref */
  #activePlayer = null;

  /** @type {AbortController|null} */
  #fetchController = null;

  /** @type {HTMLElement|null} Element that opened the modal (for focus return) */
  #modalTrigger = null;

  /** @type {number} Debounce timer for search */
  #searchTimer = 0;

  /** @type {Object} Category label map from JSON index */
  #categoryLabels = {};

  connectedCallback() {
    const section = this.querySelector('[data-videos-url]');
    if (!section) return;

    this.#perPage = parseInt(section.dataset.perPage, 10) || 24;
    const videosUrl = section.dataset.videosUrl;

    // Cache DOM refs
    this._section = section;
    this._searchInput = section.querySelector('[data-ddv-search]');
    this._filterButtons = section.querySelectorAll('[data-ddv-filter]');
    this._grid = section.querySelector('[data-ddv-grid]');
    this._pagination = section.querySelector('[data-ddv-pagination]');
    this._count = section.querySelector('[data-ddv-count]');
    this._loading = section.querySelector('[data-ddv-loading]');
    this._modal = section.querySelector('[data-ddv-modal]');
    this._modalPlayer = section.querySelector('[data-ddv-modal-player]');
    this._modalTitle = section.querySelector('[data-ddv-modal-title]');
    this._modalLink = section.querySelector('[data-ddv-modal-link]');
    this._modalClose = section.querySelector('[data-ddv-modal-close]');

    this.#init(videosUrl);
  }

  disconnectedCallback() {
    this.#fetchController?.abort();
    this.#destroyPlayer();
    clearTimeout(this.#searchTimer);
  }

  /**
   * @param {string} videosUrl - URL of the JSON video index
   */
  async #init(videosUrl) {
    try {
      this.#fetchController?.abort();
      this.#fetchController = new AbortController();

      const response = await fetch(videosUrl, { signal: this.#fetchController.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      this.#allVideos = data.videos || [];

      // Build category label map from the index
      if (data.categories) {
        for (const cat of data.categories) {
          this.#categoryLabels[cat.key] = cat.label;
        }
      }

      this.#restoreStateFromURL();
      this.#bindEvents();
      this.#applyFiltersAndRender();

      if (this._loading) this._loading.hidden = true;
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('DDV init error:', error);
      if (this._loading) {
        this._loading.innerHTML = '<p>Unable to load videos. Please try refreshing the page.</p>';
      }
    }
  }

  // ─── URL State ─────────────────────────────────────────────────────

  #restoreStateFromURL() {
    const params = new URLSearchParams(window.location.search);

    const category = params.get('category');
    if (category) {
      this.#currentFilter = `category:${category}`;
    } else {
      this.#currentFilter = 'all';
    }

    this.#currentQuery = params.get('q') || '';
    this.#currentPage = parseInt(params.get('page'), 10) || 1;

    // Sync UI state
    if (this._searchInput && this.#currentQuery) {
      this._searchInput.value = this.#currentQuery;
    }

    for (const btn of this._filterButtons) {
      const isActive = btn.dataset.ddvFilter === this.#currentFilter;
      btn.classList.toggle('ddv-filter--active', isActive);
      btn.setAttribute('aria-pressed', String(isActive));
    }
  }

  #updateURL() {
    const url = new URL(window.location.href);
    const params = url.searchParams;

    params.delete('category');

    if (this.#currentFilter !== 'all') {
      const [, value] = this.#currentFilter.split(':');
      params.set('category', value);
    }

    if (this.#currentQuery) {
      params.set('q', this.#currentQuery);
    } else {
      params.delete('q');
    }

    if (this.#currentPage > 1) {
      params.set('page', String(this.#currentPage));
    } else {
      params.delete('page');
    }

    window.history.replaceState(null, '', url.toString());
  }

  // ─── Event Binding ─────────────────────────────────────────────────

  #bindEvents() {
    // Filter buttons
    for (const btn of this._filterButtons) {
      btn.addEventListener('click', () => {
        this.#currentFilter = btn.dataset.ddvFilter;
        this.#currentPage = 1;

        for (const b of this._filterButtons) {
          const isActive = b === btn;
          b.classList.toggle('ddv-filter--active', isActive);
          b.setAttribute('aria-pressed', String(isActive));
        }

        this.#applyFiltersAndRender();
      });
    }

    // Search input (debounced)
    if (this._searchInput) {
      this._searchInput.addEventListener('input', () => {
        clearTimeout(this.#searchTimer);
        this.#searchTimer = setTimeout(() => {
          this.#currentQuery = this._searchInput.value.trim();
          this.#currentPage = 1;
          this.#applyFiltersAndRender();
        }, 300);
      });

      this._searchInput.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this._searchInput.value = '';
          this.#currentQuery = '';
          this.#currentPage = 1;
          this.#applyFiltersAndRender();
        }
      });
    }

    // Modal close button
    if (this._modalClose) {
      this._modalClose.addEventListener('click', () => this.#closeModal());
    }

    // Native <dialog> cancel event (Escape key)
    if (this._modal) {
      this._modal.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.#closeModal();
      });

      // Click on backdrop
      this._modal.addEventListener('click', (event) => {
        if (event.target === this._modal) {
          this.#closeModal();
        }
      });
    }

    // Browser back/forward
    window.addEventListener('popstate', () => {
      this.#restoreStateFromURL();
      this.#applyFiltersAndRender();
    });
  }

  // ─── Filtering & Search ────────────────────────────────────────────

  #applyFiltersAndRender() {
    let results = this.#allVideos;

    // Apply category filter
    if (this.#currentFilter !== 'all') {
      const [, value] = this.#currentFilter.split(':');
      results = results.filter(v => v.category === value);
    }

    // Apply search
    if (this.#currentQuery) {
      const tokens = this.#currentQuery.toLowerCase().split(/\s+/).filter(Boolean);
      results = results.filter(v => {
        const text = v.search_text || '';
        return tokens.every(token => text.includes(token));
      });
    }

    this.#filteredVideos = results;

    // Clamp page
    const totalPages = Math.max(1, Math.ceil(this.#filteredVideos.length / this.#perPage));
    if (this.#currentPage > totalPages) this.#currentPage = totalPages;

    this.#updateURL();
    this.#renderGrid();
    this.#renderPagination();
    this.#renderCount();
  }

  // ─── Grid Rendering ────────────────────────────────────────────────

  #renderGrid() {
    const start = (this.#currentPage - 1) * this.#perPage;
    const pageVideos = this.#filteredVideos.slice(start, start + this.#perPage);

    if (pageVideos.length === 0) {
      this._grid.innerHTML = '<div class="ddv-empty" role="status"><p>No videos match your search.</p></div>';
      return;
    }

    const cards = [];
    for (const video of pageVideos) {
      cards.push(this.#createCardHTML(video));
    }
    this._grid.innerHTML = cards.join('');

    // Bind card click events
    for (const card of this._grid.querySelectorAll('[data-video-id]')) {
      const thumb = card.querySelector('.ddv-card__thumb');
      if (!thumb) continue;

      thumb.addEventListener('click', () => this.#openModal(card.dataset.videoId, thumb));

      thumb.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.#openModal(card.dataset.videoId, thumb);
        }
      });
    }
  }

  /**
   * @param {VideoRecord} video
   * @returns {string} HTML string for a video card
   */
  #createCardHTML(video) {
    const categoryLabel = video.category_label || this.#categoryLabels[video.category] || video.category;
    const categoryBadge = video.category
      ? `<span class="ddv-card__badge ddv-card__badge--${this.#esc(video.category)}">${this.#esc(categoryLabel)}</span>`
      : '';

    const ytBadge = video.provider === 'youtube'
      ? '<span class="ddv-card__yt-badge" aria-label="YouTube video">YT</span>'
      : '';

    const date = video.published_at
      ? `<time class="ddv-card__date" datetime="${this.#esc(video.published_at)}">${this.#formatDate(video.published_at)}</time>`
      : '';

    const fallbackSrc = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='560' height='315'%3E%3Crect fill='%23222' width='560' height='315'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23666' font-size='20'%3ENo Preview%3C/text%3E%3C/svg%3E";

    return `
      <article class="ddv-card" data-video-id="${this.#esc(video.id)}" aria-labelledby="DdvCardTitle-${this.#esc(video.id)}">
        <div class="ddv-card__thumb" role="button" tabindex="0" aria-label="Play ${this.#esc(video.title)}">
          <img
            src="${this.#esc(video.poster_url || '')}"
            alt=""
            loading="lazy"
            width="560"
            height="315"
            onerror="this.src='${fallbackSrc}'"
          >
          <div class="ddv-card__play-icon" aria-hidden="true">
            <svg width="48" height="48" viewBox="0 0 48 48" focusable="false">
              <circle cx="24" cy="24" r="24" fill="rgba(0,0,0,0.6)"/>
              <polygon points="18,14 36,24 18,34" fill="white"/>
            </svg>
          </div>
          <div class="ddv-card__badges" aria-hidden="true">
            ${categoryBadge}
            ${ytBadge}
          </div>
        </div>
        <a class="ddv-card__info" href="${this.#esc(video.article_url)}" aria-label="Read the full review">
          <h3 class="ddv-card__title" id="DdvCardTitle-${this.#esc(video.id)}">${this.#esc(video.title)}</h3>
          <div class="ddv-card__meta">
            <span class="ddv-card__category">${this.#esc(categoryLabel)}</span>
            ${date}
          </div>
          <span class="ddv-card__cta">Read full article</span>
        </a>
      </article>
    `;
  }

  // ─── Pagination ────────────────────────────────────────────────────

  #renderPagination() {
    const totalPages = Math.max(1, Math.ceil(this.#filteredVideos.length / this.#perPage));

    if (totalPages <= 1) {
      this._pagination.innerHTML = '';
      return;
    }

    const parts = [];

    // Previous button
    parts.push(`<button class="ddv-pagination__btn ddv-pagination__prev" ${this.#currentPage === 1 ? 'disabled aria-disabled="true"' : ''} data-page="${this.#currentPage - 1}" aria-label="Previous page">
      <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M10 12L6 8l4-4" stroke="currentColor" stroke-width="2" fill="none"/></svg>
      <span class="ddv-pagination__prev-label">Previous</span>
    </button>`);

    // Page numbers
    const pages = this.#getPaginationRange(this.#currentPage, totalPages);
    for (const p of pages) {
      if (p === '...') {
        parts.push('<span class="ddv-pagination__ellipsis" aria-hidden="true">…</span>');
      } else {
        const isCurrent = p === this.#currentPage;
        parts.push(`<button class="ddv-pagination__btn ddv-pagination__num ${isCurrent ? 'ddv-pagination__num--active' : ''}" data-page="${p}" ${isCurrent ? 'aria-current="page"' : ''} aria-label="Page ${p}">${p}</button>`);
      }
    }

    // Next button
    parts.push(`<button class="ddv-pagination__btn ddv-pagination__next" ${this.#currentPage === totalPages ? 'disabled aria-disabled="true"' : ''} data-page="${this.#currentPage + 1}" aria-label="Next page">
      <span class="ddv-pagination__next-label">Next</span>
      <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M6 4l4 4-4 4" stroke="currentColor" stroke-width="2" fill="none"/></svg>
    </button>`);

    this._pagination.innerHTML = parts.join('');

    // Bind page clicks
    for (const btn of this._pagination.querySelectorAll('[data-page]')) {
      btn.addEventListener('click', () => {
        const page = parseInt(btn.dataset.page, 10);
        if (page >= 1 && page <= totalPages) {
          this.#currentPage = page;
          this.#applyFiltersAndRender();
          this._section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }
  }

  /**
   * @param {number} current
   * @param {number} total
   * @returns {(number|string)[]}
   */
  #getPaginationRange(current, total) {
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    if (current <= 4) {
      return [1, 2, 3, 4, 5, '...', total];
    }

    if (current >= total - 3) {
      return [1, '...', total - 4, total - 3, total - 2, total - 1, total];
    }

    return [1, '...', current - 1, current, current + 1, '...', total];
  }

  // ─── Result Count ──────────────────────────────────────────────────

  #renderCount() {
    if (!this._count) return;
    const count = this.#filteredVideos.length;
    this._count.textContent = count === 1 ? '1 video' : `${count} videos`;
  }

  // ─── Modal ─────────────────────────────────────────────────────────

  /**
   * @param {string} videoId
   * @param {HTMLElement} triggerEl - Element to return focus to on close
   */
  #openModal(videoId, triggerEl) {
    const video = this.#allVideos.find(v => v.id === videoId);
    if (!video || !this._modal) return;

    this.#modalTrigger = triggerEl;

    // Set info
    this._modalTitle.textContent = video.title;
    this._modalLink.href = video.article_url;

    // Create player
    if (video.provider === 'youtube' && video.youtube_id) {
      this.#createYouTubePlayer(video.youtube_id);
    } else if (video.provider === 'hls' && video.stream_url) {
      this.#createHLSPlayer(video.stream_url, video.poster_url);
    }

    // Show modal using native <dialog> API
    this._modal.showModal();

    // Focus the close button
    this._modalClose?.focus();
  }

  #closeModal() {
    if (!this._modal) return;

    this.#destroyPlayer();
    this._modal.close();

    // Return focus to the trigger element
    if (this.#modalTrigger) {
      this.#modalTrigger.focus();
      this.#modalTrigger = null;
    }
  }

  /**
   * @param {string} streamUrl - HLS manifest URL
   * @param {string|null} posterUrl - Poster image URL
   */
  #createHLSPlayer(streamUrl, posterUrl) {
    this._modalPlayer.innerHTML = '';

    const videoEl = document.createElement('video');
    videoEl.id = 'ddv-modal-video';
    videoEl.className = 'video-js vjs-16-9 vjs-big-play-centered';
    videoEl.setAttribute('controls', '');
    videoEl.setAttribute('playsinline', '');
    if (posterUrl) videoEl.setAttribute('poster', posterUrl);

    this._modalPlayer.appendChild(videoEl);

    const initPlayer = () => {
      if (typeof videojs === 'undefined') {
        setTimeout(initPlayer, 100);
        return;
      }

      this.#activePlayer = videojs('ddv-modal-video', {
        autoplay: true,
        fluid: true,
        responsive: true,
        sources: [{
          src: streamUrl,
          type: 'application/x-mpegURL',
        }],
      });

      this.#activePlayer.ready(function () {
        this.play().catch(() => {
          // Autoplay may be blocked — user can click play
        });
      });
    };

    initPlayer();
  }

  /**
   * @param {string} youtubeId - YouTube video ID
   */
  #createYouTubePlayer(youtubeId) {
    this._modalPlayer.innerHTML = '';

    const wrapper = document.createElement('div');
    wrapper.className = 'ddv-modal__yt-wrapper';

    const iframe = document.createElement('iframe');
    iframe.src = `https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0`;
    iframe.setAttribute('allow', 'autoplay; encrypted-media');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('title', 'YouTube video player');
    iframe.className = 'ddv-modal__yt-iframe';

    wrapper.appendChild(iframe);
    this._modalPlayer.appendChild(wrapper);

    this.#activePlayer = { type: 'youtube', iframe };
  }

  #destroyPlayer() {
    if (!this.#activePlayer) return;

    if (this.#activePlayer.type === 'youtube') {
      this.#activePlayer.iframe.src = '';
      this.#activePlayer.iframe.remove();
    } else if (typeof this.#activePlayer.dispose === 'function') {
      try {
        this.#activePlayer.pause();
        this.#activePlayer.dispose();
      } catch {
        // Player may already be disposed
      }
    }

    this.#activePlayer = null;
    this._modalPlayer.innerHTML = '';
  }

  // ─── Utilities ─────────────────────────────────────────────────────

  /**
   * @param {string} str
   * @returns {string} HTML-escaped string
   */
  #esc(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * @param {string} isoStr
   * @returns {string} Formatted date string
   */
  #formatDate(isoStr) {
    try {
      return new Date(isoStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return '';
    }
  }
}

customElements.define('deep-dive-videos', DeepDiveVideos);
